import React from 'react';
import { FC, useCallback } from 'react';
import styled, { css } from 'styled-components';
import { FormControl } from '../formControl';
import { Icon } from '../icon';
import { ZTooltip } from '../ztooltip';
import { T } from '../helpers/helpers';
import UploadIcon from '../icons/upload-solid.svg';
import RefreshIcon from '../icons/sync-solid.svg';
import { CheckNegativeSolid } from '../svg/checkNegativeSolid';

const FormControlSelectablePresets = styled(FormControl)`
    padding: 0;
`;

const PresetsGrid = styled.div<{ $columnWidthPreset: number; $gapPreset?: string }>`
    display: grid;
    grid-template-columns: repeat(auto-fill, ${({ $columnWidthPreset }) => $columnWidthPreset}px);
    gap: ${({ $gapPreset }) => $gapPreset || '10px'};
    margin-top: 2px;
`;

const PresetImageContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    width: 76px;
    height: 76px;
`;

const PresetElementContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    width: 144px;
    height: 94px;
`;

const PresetItem = styled.div`
    position: relative;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    align-items: center;
    background: white;
`;

const CheckIndicator = styled(Icon)`
    height: 20px;
    width: 20px;
    position: absolute;
    top: -5px;
    right: -5px;
    color: ${(props) => props.theme.app.secondary};
`;

const RefreshIndicator = styled(Icon)`
    height: 20px;
    width: 20px;
    position: absolute;
    background: white;
    border-radius: 10px;
    padding: 2px;
    color: #445a64;
`;

const ImageWrapper = styled.div<{ $isSelected: boolean }>`
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 72px;
    height: 72px;

    border-radius: 8px;
    border: 1px solid ${(props) => (props.$isSelected ? props.theme.app.secondary : props.theme.app.borderColor)};
    transition: all 0.2s ease;

    &:hover {
        width: ${(props) => (props.$isSelected ? '72px' : '76px')};
        height: ${(props) => (props.$isSelected ? '72px' : '76px')};
        border-color: ${(props) => (props.$isSelected ? props.theme.app.secondary : props.theme.app.borderColor)};
    }
`;

const PresetImage = styled.img`
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 8px;
`;

const PresetPlaceholder = styled.div`
    width: 100%;
    height: 100%;
    background:
        linear-gradient(45deg, #f8f9fa 25%, transparent 25%, transparent 75%, #f8f9fa 75%, #f8f9fa),
        linear-gradient(45deg, #f8f9fa 25%, transparent 25%, transparent 75%, #f8f9fa 75%, #f8f9fa);
    background-size: 8px 8px;
    background-position:
        0 0,
        4px 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #6c757d;
    font-size: 11px;
    font-weight: 500;
    text-align: center;
    padding: 8px;
`;

const PresetCaption = styled.div`
    padding: 6px 0;
    font-size: 12px;
    color: ${({ theme }) => theme.app.primary};
    text-align: center;
    line-height: 1.2;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    text-transform: capitalize;
`;

const UploadButtonContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    width: 76px;
    height: 76px;
`;

const UploadPlaceholder = styled.div`
    display: flex;
    flex-direction: column;
    border-radius: 8px;
    align-items: center;
    justify-content: center;
    width: 72px;
    height: 72px;
    border-radius: 8px;
    border: 1px solid ${(props) => props.theme.app.primary_light};
    background: #fafbfb;
    color: #6c757d;
    font-size: 12px;
    text-align: center;
    padding: 16px 8px;
    transition: all 0.2s ease;

    &:hover {
        width: 76px;
        height: 76px;
    }
`;

const UploadIconStyled = styled(Icon)`
    width: 24px;
    height: 24px;
    color: #6c757d;
    margin-bottom: 4px;
`;

const SelectableElementItem = styled.div<{ $isSelected: boolean }>`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
    width: 140px;
    height: 90px;

    border-radius: 8px;
    border: 1px solid ${(props) => (props.$isSelected ? props.theme.app.secondary : 'transparent')};
    transition: all 0.2s ease;

    &:hover {
        width: ${(props) => (props.$isSelected ? '140px' : '144px')};
        height: ${(props) => (props.$isSelected ? '90px' : '94px')};
        border-color: ${(props) => (props.$isSelected ? props.theme.app.secondary : 'transparent')};
    }
`;

const ColorGridItem = styled.div<{ color: string }>`
    width: 100%;
    height: 100%;
    background: ${(props) => props.color || '#eee'};
    border-radius: 8px;
    padding: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
    display: flex;
    flex-direction: column;
    align-items: center;
    height: 48px;
    justify-content: center;
`;

const ColorNameGridItem = styled.div<{ customStyles?: string }>`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
    height: 32px;
    margin-left: 6px;
    margin-top: 6px;
    margin-bottom: 4px;
    ${(props) =>
        props.customStyles &&
        css`
            ${props.customStyles}
        `}
`;

// Preset interface
export interface Preset {
    presetID: number;
    name: string;
    imageUrl?: string;
    thumbnailUrl?: string;
    rgbHexCode?: string;
    otherName?: string; // Optional field for additional preset name
}

export interface SelectablePresetsProps {
    className?: string;
    presets: Preset[];
    selectedPresetID?: number | null;
    selectedPresetIDs?: number[];
    multiSelect?: boolean;
    uploadedImageUrl?: string | null;
    onPresetSelect: (presetID: number) => void;
    onPresetsSelect?: (presetIDs: number[]) => void;
    onImageUpload?: () => void;
    isUploading?: boolean;
    hideUpload?: boolean;
    showCaption?: boolean; // Whether to show caption under preset images
    columnWidthPreset?: number; // Default width for preset items
    gapPreset?: string; // Gap between preset items
    title?: string;
    uploadLabel?: string;
    uploadingLabel?: string;
    refreshUploadTooltip?: string;
    isImageChild?: boolean; // Whether the single preset is an image
    colorNameCustomStyles?: string; // Custom CSS for ColorNameGridItem
}

const _SelectablePresets: FC<SelectablePresetsProps> = ({
    className,
    presets,
    selectedPresetID,
    selectedPresetIDs,
    multiSelect = false,
    uploadedImageUrl,
    onPresetSelect,
    onPresetsSelect,
    onImageUpload,
    isUploading = false,
    hideUpload = false,
    showCaption = true, // Whether to show caption under preset images
    columnWidthPreset = 76, // Default width for preset items
    gapPreset = '10px',
    title,
    uploadLabel = T._('Upload', 'Admin'),
    uploadingLabel = T._('Uploading...', 'Admin'),
    refreshUploadTooltip,
    isImageChild = true, // Whether the single preset is an image
    colorNameCustomStyles
}) => {
    const handlePresetClick = useCallback(
        (presetID: number) => {
            if (multiSelect && selectedPresetIDs && onPresetsSelect) {
                // Multi-select mode
                const isSelected = selectedPresetIDs.includes(presetID);
                let newSelectedIDs: number[];

                if (isSelected) {
                    // Remove from selection
                    newSelectedIDs = selectedPresetIDs.filter((id) => id !== presetID);
                } else {
                    // Add to selection
                    newSelectedIDs = [...selectedPresetIDs, presetID];
                }

                onPresetsSelect(newSelectedIDs);
            } else {
                // Single select mode
                onPresetSelect(presetID);
            }
        },
        [multiSelect, selectedPresetIDs, onPresetsSelect, onPresetSelect]
    );

    const handleImageUpload = useCallback(
        (e: React.MouseEvent<HTMLDivElement>) => {
            e.stopPropagation();
            if (onImageUpload) {
                onImageUpload();
            }
        },
        [onImageUpload]
    );

    return (
        <FormControlSelectablePresets label={title} className={className}>
            <PresetsGrid $columnWidthPreset={columnWidthPreset} $gapPreset={gapPreset}>
                {/* Presets */}
                {presets.map((preset) => {
                    const isSelected = multiSelect ? (selectedPresetIDs?.includes(preset.presetID) ?? false) : selectedPresetID === preset.presetID;
                    return (
                        <PresetItem key={preset.presetID} onClick={() => handlePresetClick(preset.presetID)}>
                            {isImageChild ? (
                                <PresetImageContainer>
                                    {isSelected && (
                                        <CheckIndicator>
                                            <CheckNegativeSolid></CheckNegativeSolid>
                                        </CheckIndicator>
                                    )}
                                    <ImageWrapper $isSelected={isSelected}>
                                        <PresetImage
                                            src={preset.imageUrl}
                                            alt={preset.name}
                                            onError={(e) => {
                                                const target = e.target as HTMLImageElement;
                                                target.style.display = 'none';
                                                const placeholder = target.parentElement?.querySelector('div');
                                                if (placeholder) {
                                                    placeholder.style.display = 'flex';
                                                }
                                            }}
                                        />
                                        <PresetPlaceholder style={{ display: 'none' }}>{preset.name}</PresetPlaceholder>
                                    </ImageWrapper>
                                </PresetImageContainer>
                            ) : (
                                <PresetElementContainer>
                                    {isSelected && (
                                        <CheckIndicator>
                                            <CheckNegativeSolid />
                                        </CheckIndicator>
                                    )}
                                    <SelectableElementItem $isSelected={isSelected}>
                                        <ColorGridItem color={preset.rgbHexCode || '#eee'} key={preset.presetID}></ColorGridItem>
                                        <ColorNameGridItem customStyles={colorNameCustomStyles}>
                                            <div style={{ fontSize: 12 }}>{preset.otherName || ''}</div>
                                            <div style={{ fontSize: 12 }}>{preset.name || ''}</div>
                                        </ColorNameGridItem>
                                    </SelectableElementItem>
                                </PresetElementContainer>
                            )}

                            {showCaption && <PresetCaption>{preset.name}</PresetCaption>}
                        </PresetItem>
                    );
                })}

                {/* Upload option */}
                {!hideUpload && (
                    <PresetItem onClick={!uploadedImageUrl ? handleImageUpload : () => handlePresetClick(-1)}>
                        {uploadedImageUrl ? (
                            <PresetImageContainer>
                                {((!multiSelect && !selectedPresetID) || (multiSelect && (!selectedPresetIDs || selectedPresetIDs.length == 0))) && (
                                    <CheckIndicator>
                                        <CheckNegativeSolid />
                                    </CheckIndicator>
                                )}

                                <ZTooltip title={refreshUploadTooltip} position={'top'}>
                                    <RefreshIndicator onClick={handleImageUpload}>
                                        <RefreshIcon />
                                    </RefreshIndicator>
                                </ZTooltip>

                                <ImageWrapper
                                    $isSelected={(!multiSelect && !selectedPresetID) || (multiSelect && (!selectedPresetIDs || selectedPresetIDs.length == 0))}
                                >
                                    <PresetImage src={uploadedImageUrl} alt='Uploaded background preview' />
                                </ImageWrapper>
                            </PresetImageContainer>
                        ) : (
                            <UploadButtonContainer>
                                <UploadPlaceholder>
                                    <UploadIconStyled>
                                        <UploadIcon />
                                    </UploadIconStyled>
                                    {isUploading ? <span>{uploadingLabel}</span> : <span>{uploadLabel}</span>}
                                </UploadPlaceholder>
                            </UploadButtonContainer>
                        )}
                    </PresetItem>
                )}
            </PresetsGrid>
        </FormControlSelectablePresets>
    );
};

// Export selectablePresets component
export const SelectablePresets = styled(_SelectablePresets)``;
